import { Component, HostBinding, ViewChild } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, ExcludedContentOperationType, HelpIndex, UpdateStatusType, SettingsType } from '../../../../models/enums';
import { ExcludedContent } from '../../../../models/excluded-content';
import { CellTemplateType, GenericColDef } from '../../../../models/grid-context';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { ExcludedContentModalComponent } from '../../../modals/excluded-content-modal/excluded-content-modal.component';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { ObjectActionListType, Permissions, ActionType } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-excluded-content',
    templateUrl: './excluded-content.component.html',
    styleUrls: ['./excluded-content.component.scss']
})
export class ExcludedContentComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    isDirty: boolean;
    validChanges = true;
    helpIndex: any;

    gridName = 'Excluded Content';
    automationId = 'excluded-content';
    isBusy = false;
    isBusyMessage = '';
    currentHandle = '';
    updateStatusType = UpdateStatusType.EXCLUDED_CONTENT;

    // permissions
    permissionType: ObjectActionListType;
    hasExcludedContentViewPermissions: boolean;
    hasContextMenuPermissions = true;

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Name',
            field: 'name',
            automationId: this.automationId,
            lockVisible: true
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId
        },
        {
            headerName: 'Content',
            field: 'emailSignatureText',
            automationId: this.automationId,
        },
        {
            headerName: 'Created By',
            field: 'createdBy',
            automationId: this.automationId,
            suppressSizeToFit: true
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );

    rowClassRules = {
        'disabled-row': () => {
            return this.hasExcludedContentViewPermissions;
        }
    };

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.projectManager_loadEmailSignatures,
        responseName: 'emailSignatures'
    });

    isSystemUser = false;

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: this.automationId,
            click: (item) => this.editContent(item)
        }),
        new ContextMenuOption({
            name: 'Duplicate', iconClass: 'fa-copy', iconColor: 'color-grey-10', automationId: this.automationId,
            click: (item) => this.duplicateContent(item)
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: this.automationId,
            click: (item) => this.deleteContent(item), enabled: (item) => (item.createdBy !== 'System')
        })
    ];

    responseArrayCallback = (value: any) => new ExcludedContent(value);

    cellDoubleClickedCallback = (rowNode: RowNode) => {
        if (rowNode && rowNode.data instanceof ExcludedContent) {
            this.editContent(rowNode.data);
        }
    }

    constructor(private commonService: CommonService,
        private restService: RestService) {
    }

    deleteContent(item: ExcludedContent): void {
        Object.assign(item, { systemScope: this.isSystemUser });
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: item });
    }

    duplicateContent(item: ExcludedContent): void {
        this.commonService.openModal(ExcludedContentModalComponent,
            { backdrop: false, modalData: { operationType: ExcludedContentOperationType.duplicate_excluded_content_block, data: item, currentHandle: this.currentHandle, systemScope: this.isSystemUser } }).then((result) => {
                if (result.modalResult) {
                    this.genericGrid.loadData();
                }
            });
    }

    editContent(item: ExcludedContent): void {
        this.commonService.openModal(ExcludedContentModalComponent,
            { backdrop: false, modalData: { operationType: ExcludedContentOperationType.edit_excluded_content_block, data: item, currentHandle: this.currentHandle, systemScope: this.isSystemUser } }).then((result) => {
                if (result.modalResult) {
                    this.genericGrid.loadData();
                }
            });
    }

    addNewContentBlock(): void {
        this.commonService.openModal(ExcludedContentModalComponent,
            { backdrop: false, modalData: { operationType: ExcludedContentOperationType.new_excluded_content_block, currentHandle: this.currentHandle, systemScope: this.isSystemUser } }).then((result) => {
                if (result.modalResult) {
                    this.genericGrid.loadData();
                }
            });
    }

    selectedTreeItemChanged = (node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType) => {
        this.currentHandle = node.data instanceof Template ? node.data.handle : projectHandle;
        this.helpIndex = projectHandle ? HelpIndex.case_email_signatures_dlg : (settingsType === SettingsType.organization ? HelpIndex.org_email_sig_template_dlg : HelpIndex.sys_template_excluded_content_dlg);
        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        this.permissionType = projectHandle ? ObjectActionListType.PRJ_EXCLUDED_CONTENT : this.isSystemUser ? ObjectActionListType.SYSTEM_EXCLUDED_CONTENT : ObjectActionListType.ORG_EXCLUDED_CONTENT;
        this.hasExcludedContentViewPermissions = !Permissions.checkUserPermission(this.permissionType, ActionType.CREATE);

        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (this.hasExcludedContentViewPermissions) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
                this.hasContextMenuPermissions = false;
            }
        }

        if (projectHandle) {
            this.contextMenuOptions[2].permissions = [{ permissionType: this.permissionType, permissionActions: [ActionType.DELETE] }];
        }

        if (this.currentHandle) {
            this.requestInfo.data = {
                ownerHandle: this.currentHandle,
                systemScope: this.isSystemUser
            };
            this.genericGrid.loadData();
        }
    }

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }
}
